import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  apiClient,
  ApiError,
  type OrgSkillSummary,
  type PersonalSkillSummary,
} from '../lib/api';
import { canApprove, getSession } from '../lib/auth';
import { useOrgSse } from '../lib/sse';
import { ImportDialog } from '../components/ImportDialog';

type Tab = 'mine' | 'org';

function relativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const sec = Math.round(ms / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h`;
  const d = Math.round(hr / 24);
  if (d < 30) return `${d}d`;
  const mo = Math.round(d / 30);
  if (mo < 12) return `${mo}mo`;
  return `${Math.round(mo / 12)}y`;
}

export function Library() {
  const navigate = useNavigate();
  const session = getSession();
  const orgId = session?.orgId ?? null;
  const [tab, setTab] = useState<Tab>('mine');
  const [skills, setSkills] = useState<PersonalSkillSummary[] | null>(null);
  const [orgSkills, setOrgSkills] = useState<OrgSkillSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showImport, setShowImport] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    apiClient.skills
      .list()
      .then((data) => {
        if (!cancelled) setSkills(data);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : 'Failed to load skills');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const refetchOrgSkills = useCallback(() => {
    if (!orgId) return;
    apiClient.orgs
      .listSkills(orgId)
      .then((data) => setOrgSkills(data.skills))
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err.message : 'Failed to load team skills');
      });
  }, [orgId]);

  useEffect(() => {
    if (!orgId || tab !== 'org') return;
    refetchOrgSkills();
  }, [orgId, tab, refetchOrgSkills]);

  useOrgSse(orgId, { enabled: tab === 'org', onHint: refetchOrgSkills });

  const filtered = useMemo(() => {
    if (!skills) return null;
    const q = search.trim().toLowerCase();
    if (!q) return skills;
    return skills.filter(
      (s) =>
        s.name.toLowerCase().includes(q) ||
        (s.description ?? '').toLowerCase().includes(q),
    );
  }, [skills, search]);

  const filteredOrg = useMemo(() => {
    if (!orgSkills) return null;
    const q = search.trim().toLowerCase();
    if (!q) return orgSkills;
    return orgSkills.filter((s) => s.skillPath.toLowerCase().includes(q));
  }, [orgSkills, search]);

  return (
    <div className="grid h-full grid-cols-1 md:grid-cols-[280px_1fr]">
      <aside className="hairline-b md:hairline-b-0 md:hairline-r flex flex-col bg-surface">
        {/* List header */}
        <div className="flex items-center justify-between px-3 pt-3 pb-2">
          <div className="flex items-baseline gap-2">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-text-muted">
              Library
            </span>
            <span className="font-mono text-[11px] text-text-subtle">
              {skills?.length ?? '·'}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setShowImport(true)}
              className="btn btn-icon"
              title="Import from GitHub"
            >
              ↓
            </button>
            <button
              type="button"
              onClick={() => navigate('/create')}
              className="btn btn-icon"
              title="New skill"
            >
              +
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="px-3 pb-2">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search…"
            className="block w-full rounded border bg-canvas px-2 py-1 font-mono text-[12px] outline-none focus:border-[var(--color-accent)]"
          />
        </div>

        {orgId && (
          <div className="px-3 pb-2">
            <div className="hairline-b inline-flex gap-1">
              <button
                type="button"
                onClick={() => setTab('mine')}
                className={
                  tab === 'mine'
                    ? 'border-b-2 border-[var(--color-accent)] px-2 py-1 text-[11px] font-semibold uppercase tracking-wider'
                    : 'px-2 py-1 text-[11px] font-semibold uppercase tracking-wider text-text-subtle hover:text-text-default'
                }
              >
                Mine
              </button>
              <button
                type="button"
                onClick={() => setTab('org')}
                className={
                  tab === 'org'
                    ? 'border-b-2 border-[var(--color-accent)] px-2 py-1 text-[11px] font-semibold uppercase tracking-wider'
                    : 'px-2 py-1 text-[11px] font-semibold uppercase tracking-wider text-text-subtle hover:text-text-default'
                }
              >
                Org
              </button>
            </div>
            {tab === 'org' && canApprove(session) && (
              <Link
                to={`/orgs/${encodeURIComponent(orgId)}/inbox`}
                className="mt-2 block text-[11px] text-[var(--color-accent)] hover:underline"
              >
                → Review inbox
              </Link>
            )}
          </div>
        )}

        {/* Smart Views — placeholder shortcuts that route to filtered library views */}
        <div className="px-3 py-1">
          <button
            type="button"
            className="flex w-full items-center justify-between rounded px-2 py-1 text-left text-[13px] text-text-muted hover:bg-surface-2"
          >
            <span className="flex items-center gap-2">
              <span className="text-text-subtle">★</span>
              Favorites
            </span>
            <span className="font-mono text-[11px] text-text-subtle">—</span>
          </button>
          <button
            type="button"
            className="flex w-full items-center justify-between rounded px-2 py-1 text-left text-[13px] text-text-muted hover:bg-surface-2"
          >
            <span className="flex items-center gap-2">
              <span className="text-text-subtle">⏱</span>
              Recent
            </span>
            <span className="font-mono text-[11px] text-text-subtle">
              {skills?.length ?? '—'}
            </span>
          </button>
        </div>

        {/* Bundle / Ungrouped — v1 has no bundles yet, all skills go under UNGROUPED */}
        <div className="mt-2 px-3 py-1">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-text-subtle">
            Ungrouped
          </div>
        </div>

        <ul className="flex-1 overflow-y-auto pb-3">
          {tab === 'mine' &&
            filtered?.map((skill) => (
              <li key={skill.id}>
                <Link
                  to={`/skill/${encodeURIComponent(skill.id)}`}
                  className="grid grid-cols-[8px_1fr_auto] items-center gap-2 px-4 py-[5px] text-[13px] hover:bg-surface-2"
                >
                  <span
                    className="h-[6px] w-[6px] rounded-full"
                    style={{ background: 'var(--color-success)' }}
                  />
                  <span className="truncate font-mono">{skill.name}</span>
                  <span className="font-mono text-[11px] text-text-subtle">
                    {relativeTime(skill.updatedAt)}
                  </span>
                </Link>
              </li>
            ))}
          {tab === 'mine' && filtered && filtered.length === 0 && (
            <li className="px-4 py-3 text-[12px] text-text-subtle">
              {search ? 'No matches.' : 'No skills yet — click + to create one.'}
            </li>
          )}
          {tab === 'org' && orgId && filteredOrg === null && (
            <li className="px-4 py-3 text-[12px] text-text-subtle">Loading…</li>
          )}
          {tab === 'org' &&
            orgId &&
            filteredOrg?.map((skill) => (
              <li key={skill.id}>
                <Link
                  to={`/orgs/${encodeURIComponent(orgId)}/skills/${encodeURIComponent(skill.id)}`}
                  className="grid grid-cols-[8px_1fr_auto] items-center gap-2 px-4 py-[5px] text-[13px] hover:bg-surface-2"
                >
                  <span
                    className="h-[6px] w-[6px] rounded-full"
                    style={{ background: 'var(--color-accent)' }}
                  />
                  <span className="truncate font-mono">{skill.skillPath}</span>
                  <span className="font-mono text-[11px] text-text-subtle">
                    {relativeTime(skill.lastModifiedAt)}
                  </span>
                </Link>
              </li>
            ))}
          {tab === 'org' && filteredOrg && filteredOrg.length === 0 && (
            <li className="px-4 py-3 text-[12px] text-text-subtle">
              {search ? 'No matches.' : 'No team skills yet.'}
            </li>
          )}
        </ul>

        {skills === null && !error && (
          <p className="px-4 pb-3 text-[12px] text-text-subtle">Loading…</p>
        )}
        {error && (
          <p className="mx-3 mb-3 rounded border bg-danger-soft p-2 text-[11px] text-[var(--color-danger)]">
            {error}
          </p>
        )}
      </aside>

      <section className="hidden flex-col items-center justify-center bg-canvas md:flex">
        <div className="max-w-sm text-center">
          <p className="text-sm text-text-muted">Select a skill on the left.</p>
          <p className="mt-1 text-[12px] text-text-subtle">
            Or hit{' '}
            <button
              onClick={() => navigate('/create')}
              className="underline hover:text-[var(--color-accent)]"
            >
              + new
            </button>{' '}
            to start one with AI.
          </p>
        </div>
      </section>

      {showImport && (
        <ImportDialog
          onClose={() => setShowImport(false)}
          onImported={(id) => {
            setShowImport(false);
            navigate(`/skill/${encodeURIComponent(id)}`);
          }}
        />
      )}
    </div>
  );
}
